import { Box, Flex, Grid, useBreakpointValue } from '@chakra-ui/react'
import { useEffect, useMemo } from 'react'
import { Image as ImageHOC } from '@/components/HOC/Image'
import GameBg from '@/public/assets/images/runes/bg.png'
import { useRunesStore } from './RunesProvider'
import { LastGames } from './LastGames'
import { Form } from './Form'
import { useBalanceStore } from '@/context/UserBalance'
import { useShallow } from 'zustand/react/shallow'
import { Paytable } from './Paytable'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { AnimatePresence } from 'framer-motion'
import Head from 'next/head'
import { GameStatus } from './types'
import { RunesBanner } from './ContestBanner'
import { BlurEffect, GameItem } from './GameItem'
import { RUNSES } from './utils'

export const RunesTemplate = () => {
  const { isInitialized } = useRunesStore()

  const denom = useBalanceStore((state) => state.denom)
  useEffect(() => {
    useRunesStore.getState().setValue('denom', denom)
  }, [denom])

  useEffect(() => {
    if (!isInitialized) {
      const formSessionValue = sessionStorage.getItem('runes-form')
      if (formSessionValue) {
        const values = Object.fromEntries(
          Object.entries(JSON.parse(formSessionValue))
            .filter((x) => x[1])
            .map(([key, value]) => [key, { value, setDirty: false }])
        )
        useRunesStore.getState().setValues(values)
      }
    }
  }, [])

  return (
    <>
      {/* <RunesBanner /> */}
      <Grid gridTemplateColumns={{ base: '1fr', xl: 'auto 320px' }} gap={5}>
        <Box p={{ base: 0, md: 5 }} rounded={'2xl'} pos={'relative'} overflow={'hidden'}>
          <ImageHOC
            fill={'auto'}
            quality={90}
            objectFit="cover"
            priority={true}
            loading="eager"
            src={GameBg}
            alt={''}
          />
          <Box pos={'relative'} zIndex={10}>
            <Box p={4}>
              <Paytable />
              <GamePanel />
            </Box>
            <Box>
              <Form />
            </Box>
          </Box>
        </Box>
        <LastGames />
      </Grid>
    </>
  )
}

const GamePanel = () => {
  const gameResult = useRunesStore(useShallow((state) => state.view.gameResult.items))
  const gameStatus = useRunesStore(useShallow((state) => state.status))

  const { volumeFadeDown } = useAudio()

  useEffect(() => {
    if (gameResult[0] != 0 && gameResult.length > 0) {
      volumeFadeDown(SOUNDS.RUNES_REVAL)
    }
  }, [gameResult.length])

  const fillValue = useMemo(() => {
    return [
      false,
      ...Array.from({ length: 7 }, (_, item: number) =>
        gameResult.filter((x) => x - 1 == item).length > 1 ? true : false
      ),
    ]
  }, [gameResult.join(',')])

  const firstRowCount = useBreakpointValue({ base: 3, md: 5 }) || 0

  const paddedResult = [...gameResult, ...Array(5 - gameResult.length).fill(0)].slice(0, 5)
  const firstRow = paddedResult.slice(0, firstRowCount)
  const secondRow = paddedResult.slice(firstRowCount)

  return (
    <>
      <Head>
        {RUNSES.map((src) => (
          <link key={src.src.src as string} rel="preload" href={src.src.src} as="image" />
        ))}
      </Head>
      <Flex direction="column" align="center" mt={{ base: 6, sm: 8 }} mb={{ base: 2, sm: 4 }} gap={4}>
        <Flex justify="center" flexWrap="nowrap" gap={4}>
          {firstRow.map((_, idx) => (
            <Box
              key={idx}
              h={[20, 32, 32]}
              w={[20, 32, 32]}
              my={[0, 4]}
              borderRadius={['24px', '24px', '40px']}
              boxShadow="0px -3.216px 9.647px rgba(255, 255, 255, 0.20) inset, 0px -9.647px 32.158px rgba(161, 164, 252, 0.32) inset"
            >
              <BlurEffect index={idx} waiting={gameStatus == GameStatus.ACTIVE && gameResult[idx] == 0}>
                <AnimatePresence>
                  {gameResult[idx] > 0 && (
                    <GameItem
                      key={idx}
                      item={RUNSES[gameResult[idx] || 0]}
                      isActive={fillValue[gameResult[idx] || 0]}
                    />
                  )}
                </AnimatePresence>
              </BlurEffect>
            </Box>
          ))}
        </Flex>

        {secondRow.length > 0 && (
          <Flex justify="center" flexWrap="nowrap" gap={4}>
            {secondRow.map((_, idx) => (
              <Box
                key={idx + firstRowCount}
                h={[20, 32, 32]}
                w={[20, 32, 32]}
                borderRadius={['24px', '24px', '40px']}
                boxShadow="0px -3.216px 9.647px rgba(255, 255, 255, 0.20) inset, 0px -9.647px 32.158px rgba(161, 164, 252, 0.32) inset"
              >
                <AnimatePresence>
                  <BlurEffect index={idx + firstRowCount} waiting={gameResult[idx + firstRowCount] == 0}>
                    {gameResult[idx + firstRowCount] > 0 && (
                      <GameItem
                        key={idx + firstRowCount}
                        item={RUNSES[gameResult[idx + firstRowCount] || 0]}
                        isActive={fillValue[gameResult[idx + firstRowCount] || 0]}
                      />
                    )}
                  </BlurEffect>
                </AnimatePresence>
              </Box>
            ))}
          </Flex>
        )}
      </Flex>
    </>
  )
}
